<script setup lang="ts">
import LegacyProgressBar from '@/components/current-student-advanced-standing-form/shared/LegacyProgressBar.vue'
import type { LegacyClassBinding } from '@/components/current-student-advanced-standing-form/shared/types'

defineProps<{
  impersonateMessageVisible: boolean
  pageLoadMessageClass: LegacyClassBinding
  pageLoadMessage: string
  studentDetailsVisible: boolean
  username: string
  password: string
  loginPending: boolean
  loginErrorMessage: string
  loginProgressVisible: boolean
  loginProgressClass: LegacyClassBinding
  loginProgressMessage: string
}>()

const emit = defineEmits<{
  'update:username': [value: string]
  'update:password': [value: string]
  'loginInput': []
}>()

function onUsernameInput(event: Event): void {
  emit('update:username', (event.target as HTMLInputElement).value)
  emit('loginInput')
}

function onPasswordInput(event: Event): void {
  emit('update:password', (event.target as HTMLInputElement).value)
  emit('loginInput')
}
</script>

<template>
  <fieldset>
    <legend>
      <h1>La Trobe University Advanced Standing Application</h1>
    </legend>
    <p>Once you login to this form, all your changes will be saved as you go. You may close the form at any step and return to it later by logging back into your application.</p>
    <p>Once you have submitted the application, you may return to your application to edit your submission and resubmit. However, it is recommended that you include as much detail as possible in your first submission to ensure prompt assessment of your application.</p>

    <h4>Supporting Documentation</h4>
    <p>For all Advanced Standing (credit) requests based on studies undertaken at an institution other than La Trobe University, please attach official academic transcripts and detailed subject descriptions for each of the requests.</p>

    <h4>Enrolment Verification</h4>
    <p>
      Students are encouraged to check their contact and enrolment details via
      <a href="http://www.latrobe.edu.au/students/admin/studentonline" target="_blank" rel="noreferrer">StudentOnLine</a>
      prior to the census date in each teaching period. Census date information can be viewed
      <a target="_blank" href="http://www.latrobe.edu.au/calendar/all-students/census-dates" rel="noreferrer">here</a>.
      To lodge an appeal regarding the Advanced Standing process, refer to the
      <a href="https://policies.latrobe.edu.au/document/view.php?id=390" target="_blank" rel="noreferrer">Credit Policy</a>.
    </p>

    <h4>Government Allowance</h4>
    <p>If you receive Centrelink benefits you are responsible for advising the appropriate government department of any enrolment variations. A change in course load may affect your entitlement.</p>

    <p>
      If you experience any difficulties completing this form please contact
      <a href="http://latrobe-current.custhelp.com/app/home" target="_blank" rel="noreferrer">ASK La Trobe.</a>
      Please note that it may take up to 24 hours for new student accounts to become active.
    </p>

    <h4>Courses designated for entry into first year only</h4>
    <p>
      The University will designate courses where there is only admission into Year 1 of the course. In these cases, there is limited advanced standing.
      <a target="_blank" href="https://www.latrobe.edu.au/students/getting-started/new-students/advanced-standing/advanced-standing-limited-entry-courses" rel="noreferrer">See list of limited entry courses.</a>
    </p>

    <div
      class="alert alert-info"
      id="impersonateMessage"
      :class="{ hidden: !impersonateMessageVisible }"
    >
      <h3>Impersonating a Student</h3>
      <p>This feature is available to staff members who are logged in with the impersonate student permission.</p>
    </div>
    <div id="maintenanceModeMessageBox"></div>
    <div
      class="alert"
      id="pageLoadErrorBox"
      :class="pageLoadMessageClass"
      v-html="pageLoadMessage"
    ></div>
  </fieldset>

  <fieldset id="student_details" :class="{ hidden: !studentDetailsVisible }">
    <legend>Student Details</legend>

    <div class="form-field requiredinput">
      <label for="StudentUsernameInput">La Trobe Student Username:</label>
      <input
        id="StudentUsernameInput"
        :value="username"
        :disabled="loginPending"
        type="text"
        class="form-control"
        autocomplete="username"
        data-admissions-name="StudentUsername"
        data-admissions-group="StudentInfo"
        @input="onUsernameInput"
      >
    </div>

    <div class="form-field requiredinput">
      <label for="StudentPasswordInput">Password:</label>
      <input
        id="StudentPasswordInput"
        :value="password"
        :disabled="loginPending"
        type="password"
        class="form-control"
        :autocomplete="impersonateMessageVisible ? 'one-time-code' : 'current-password'"
        data-admissions-name="StudentPassword"
        data-admissions-group="StudentInfo"
        @input="onPasswordInput"
      >
    </div>

    <div class="alert alert-danger" id="loginPageError">{{ loginErrorMessage }}</div>

    <LegacyProgressBar
      id="validateProgress"
      :visible="loginProgressVisible"
      :bar-class="loginProgressClass"
      :message="loginProgressMessage"
    />
  </fieldset>
</template>
